import { useCallback, useState } from 'react';
import type { AnalysisType, SegmentationControllerType, SegmentationProps } from 'config/analysisConfig';
import { getOffSegmentation, getSegShapeAnalysisType } from 'config/analysisConfig';
import { configStore } from 'config/configStore';
import type { volumeColorType } from 'config/colorUtils';
import type { ToolId } from 'config/toolDefinitionsConfig';
import { scheduleRender } from '../../rendering/Panels/renderState';
import { updateLabelmapSegment } from '../../rendering/Panels/Renderers/CornerstoneManager';
import { LightingOptions } from 'config/config';

type SegmentationMap = Record<string, SegmentationProps>;

type RenderModes = {
  heartCut: boolean;
  ventricle: boolean;
  coronaryAorta: boolean;
  MIP: boolean;
  colorType: volumeColorType;
};

export function useSegmentationShapeController({
  analysisType,
  config,
  setData,
  segmentation,
  segmentationReady,
  coronaryReady,
  hasVR,
  updatePanelState,
  waitingAction,
  closeLoadingModal,
  handleToolActivate,
}: {
  analysisType: AnalysisType;
  config: {
    heartCut?: boolean;
    ventricle?: boolean;
    coronaryAorta?: boolean;
    visibleMIP?: boolean;
    vrType: volumeColorType;
    lightingOptions:LightingOptions;
  };
  setData: (data: Record<string, unknown>) => void;
  segmentation: SegmentationMap;
  segmentationReady: boolean;
  coronaryReady: boolean;
  hasVR: boolean;
  updatePanelState: (...args: any[]) => void;
  waitingAction: (args: { message: string }) => void;
  closeLoadingModal: () => void;
  handleToolActivate: (toolId: ToolId) => void;
}) {
  const isSegmentationShape = getSegShapeAnalysisType(analysisType);
  const [modes, setRenderModes] = useState<RenderModes>({
    heartCut: config.heartCut || false,
    ventricle: config.ventricle || false,
    coronaryAorta: config.coronaryAorta || false,
    MIP: config.visibleMIP || false,
    colorType: configStore.get().vrType,
  });


   const handlelightingChange = useCallback(
     (params: { value: number; enabled: boolean }) => {
       scheduleRender(hasVR ? 'VR' : 'Surface3D', {
         type: 'lightingChange',
         ambient: params.value,
         enabled:params.enabled,
       });
     },
     [config.lightingOptions, hasVR],
   );

  function updateSegmentation(mode: 'ventricle' | 'coronaryAorta' | 'la') {
    if (!segmentation) return;
    const offSegs = getOffSegmentation(mode);
    const newSegmentation = { ...segmentation };
    const segKeys = Object.keys(segmentation) as SegmentationControllerType[];

    let changed = false;
    for (const key of segKeys) {
      const nextVis = !offSegs.includes(key);
      if (newSegmentation[key].visibility !== nextVis) {
        newSegmentation[key] = { ...newSegmentation[key], visibility: nextVis };
        updatePanelState(key, 'segmentation', {
          ...newSegmentation,
        });
        updateLabelmapSegment({
          segmentTarget: key,
          segmentations: newSegmentation,
        });
        changed = true;
      }
    }
    if (hasVR && changed) {
      scheduleRender('VR', { type: 'updateSegmentation', newSegmentation });
    } else {
      scheduleRender('Surface3D', { type: 'updateLabelmap' });
    }
  }

  const toggleSegmentationGroup = useCallback(
    async (mode: 'ventricle' | 'coronaryAorta' | 'la') => {
      if (!segmentation || !isSegmentationShape) return;

      if(mode == 'la'){
        updateSegmentation(mode);
        setData({laCapture:true, laText: true});
        return;
      }

      const isVentricleMode = mode === 'ventricle';
      const isCoronaryAortaMode = mode === 'coronaryAorta';
      if (
        (isVentricleMode && !segmentationReady) ||
        (isCoronaryAortaMode && !coronaryReady)
      ) {
        return;
      }

      setData({
        visibleMIP: false,
        ventricle: isVentricleMode,
        coronaryAorta: isCoronaryAortaMode,
        laCapture: false,
        laText: false,
      });

      setRenderModes((prev) => ({
        ...prev,
        MIP: false,
        ventricle: isVentricleMode,
        coronaryAorta: isCoronaryAortaMode,
      }));
      updateSegmentation(mode);
    },
    [
      closeLoadingModal,
      coronaryReady,
      hasVR,
      isSegmentationShape,
      segmentation,
      segmentationReady,
      setData,
      updatePanelState,
      waitingAction,
    ],
  );

  const toggleHeartCutter = useCallback(() => {
    if (!isSegmentationShape) return;
    const hc = !config.heartCut;
    setData({ visibleMIP: false, heartCut: hc });
    setRenderModes((prev) => ({
      ...prev,
      heartCut: hc,
      MIP: false,
    }));

    if (hasVR) {
      scheduleRender('VR', {
        type: 'applySegVolumeMode',
        mode: 'runHeartCutter',
      });
    } else {
      scheduleRender('Surface3D', { type: 'runHeartCutter', mode: 'toggle' });
    }
  }, [config.heartCut, hasVR, isSegmentationShape, setData]);

  const toggleVolumeColorType = useCallback(
    (mode: volumeColorType) => {
      if (!isSegmentationShape || !hasVR) return;
      configStore.setData({ vrType: mode, visibleMIP: false });
      setRenderModes((prev) => ({
        ...prev,
        MIP: false,
        colorType: mode,
      }));
      if (mode === 'MIP') {
        scheduleRender('VR', { type: 'applySegVolumeMode', mode: 'runMIP' });
      } else {
        scheduleRender('VR', {
          type: 'applySegVolumeMode',
          mode: 'updateVolumeColor',
          colorType: mode,
        });
      }
    },
    [hasVR, isSegmentationShape],
  );

  const handleToggleHeartCutTool = useCallback(() => {
    if (!segmentationReady) return;
    toggleHeartCutter();
    handleToolActivate('ToggleHeartCut');
  }, [handleToolActivate, segmentationReady, toggleHeartCutter]);

  const handleToggleMipTool = useCallback(() => {
    if (!isSegmentationShape || !hasVR) return;
    const vm = !config.visibleMIP;
    setData({
      visibleMIP: vm,
    });
    setRenderModes((prev) => ({
      ...prev,
      MIP: vm,
    }));
    scheduleRender('VR', {
      type: 'applySegVolumeMode',
      mode: 'runMIP',
    });
    handleToolActivate('ToggleMIP');
    handleToolActivate('Rotation');
  }, [config.visibleMIP, handleToolActivate, hasVR, isSegmentationShape, setData]);

  return {
    isSegmentationShape,
    modes,
    toggleSegmentationGroup,
    toggleHeartCutter,
    toggleVolumeColorType,
    handleToggleHeartCutTool,
    handleToggleMipTool,
    handlelightingChange,
  };
}
